/**
 * Página de Alertas — Sprint 15.3.
 *
 * v2: refactor de "buzón cronológico" a "centro de comando".
 * - Header con métricas vivas (críticas hoy / esta semana / informativas).
 * - Lista agrupada por bucket con cards accionables.
 * - Acciones por card: CTA primario, "Atendida" (ack), "Posponer 1 semana".
 * - Toggle "Mostrar histórico" trae alertas atendidas/snoozed.
 *
 * Telemetría (Sprint 15.4 la consolida en 1 PR aparte):
 * - alerts_viewed: dispara al primer render con datos.
 * - alert_cta_clicked: cuando el usuario abre el CTA primario.
 * - alert_acked / alert_snoozed: cuando ejecuta una acción.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  ApiError,
  fetchAlerts,
  fetchAlertsSummary,
  patchAlertAction,
  type AlertDTO,
  type AlertSummary,
} from "@/lib/api";
import { alertTypeLabel } from "@/lib/alert-labels";
import { track } from "@/lib/analytics";
import { formatDateEs } from "@/lib/dates";
import { useActiveMunicipalityDane } from "@/lib/municipality";

type Bucket = "hoy" | "semana" | "info";

const SEVERITY_PALETTE: Record<AlertDTO["severity"], string> = {
  info: "bg-brand-50 text-brand-700 ring-brand-200",
  warning: "bg-amber-50 text-amber-800 ring-amber-200",
  critical: "bg-red-50 text-red-800 ring-red-200",
};

const SEVERITY_LABELS: Record<AlertDTO["severity"], string> = {
  info: "Info",
  warning: "Atención",
  critical: "Crítico",
};

const SEVERITY_ICONS: Record<AlertDTO["severity"], string> = {
  info: "ⓘ",
  warning: "▲",
  critical: "●",
};

const BUCKET_LABELS: Record<Bucket, string> = {
  hoy: "Hoy",
  semana: "Esta semana",
  info: "Informativas",
};

function formatRelative(iso: string): string {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffH = diffMs / 36e5;
    if (diffH < 1) return "hace menos de 1h";
    if (diffH < 24) return `hace ${Math.floor(diffH)}h`;
    const diffD = Math.floor(diffH / 24);
    return diffD === 1 ? "hace 1 día" : `hace ${diffD} días`;
  } catch {
    return iso;
  }
}

/** Decide en qué bucket cae cada alerta. Espeja la lógica del backend
 * (subsystems/alerts/repository.py _SUMMARY_SQL). */
function bucketize(a: AlertDTO): Bucket {
  const now = Date.now();
  const expMs = a.expires_at ? new Date(a.expires_at).getTime() - now : null;
  const createdToday =
    a.expires_at === null && new Date(a.created_at).toDateString() === new Date().toDateString();

  if (a.severity === "critical" && ((expMs !== null && expMs < 48 * 3_600_000) || createdToday)) {
    return "hoy";
  }
  if (expMs !== null && expMs < 7 * 24 * 3_600_000) {
    return "semana";
  }
  if (a.severity === "critical" || a.severity === "warning") {
    return "semana";
  }
  return "info";
}

/** Pospuesta vigente: sin atender y con `snoozed_until` en el futuro. El
 * feed activo del backend ya las excluye; solo llegan con el historico
 * (#563) y no deben pintarse como activas. */
function isSnoozed(a: AlertDTO): boolean {
  return (
    a.atendida_at === null &&
    a.snoozed_until !== null &&
    new Date(a.snoozed_until).getTime() > Date.now()
  );
}

export function Alertas() {
  const activeDane = useActiveMunicipalityDane();
  const [includeAttended, setIncludeAttended] = useState(false);
  const [bucketFilter, setBucketFilter] = useState<Bucket | null>(null);
  const viewedRef = useRef(false);

  const alertsQuery = useQuery({
    queryKey: ["alerts", activeDane, includeAttended],
    queryFn: () => fetchAlerts(activeDane, { includeAttended }),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 2;
    },
  });

  const summaryQuery = useQuery({
    queryKey: ["alerts-summary", activeDane],
    queryFn: () => fetchAlertsSummary(activeDane),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 2;
    },
    // Refresh cada 60s para que la banda se actualice si el usuario
    // se queda en la pestaña por un rato y el cron genera alertas.
    staleTime: 60_000,
  });

  // Telemetría: dispara una sola vez por (dane, sesión) al primer render con data.
  useEffect(() => {
    if (alertsQuery.data && !viewedRef.current) {
      track({
        event_name: "alerts_viewed",
        properties: {
          dane_code: activeDane,
          total_active: alertsQuery.data.total,
          critical_today: summaryQuery.data?.critical_today ?? 0,
        },
      });
      viewedRef.current = true;
    }
  }, [alertsQuery.data, summaryQuery.data, activeDane]);

  // Agrupar por bucket — solo activas (no atendidas, no snoozed). Si
  // includeAttended=true, mostramos también las atendidas y las pospuestas,
  // cada una en su propia sección (#563).
  const grouped = useMemo(() => {
    const items = alertsQuery.data?.items ?? [];
    const buckets: Record<Bucket, AlertDTO[]> = { hoy: [], semana: [], info: [] };
    const attended: AlertDTO[] = [];
    const snoozed: AlertDTO[] = [];
    for (const a of items) {
      if (a.atendida_at) {
        attended.push(a);
      } else if (isSnoozed(a)) {
        snoozed.push(a);
      } else {
        buckets[bucketize(a)].push(a);
      }
    }
    return { buckets, attended, snoozed };
  }, [alertsQuery.data]);

  const filteredBuckets: Bucket[] = bucketFilter
    ? [bucketFilter]
    : (["hoy", "semana", "info"] satisfies Bucket[]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Alertas</h1>
        <p className="mt-1 text-sm text-slate-600">
          Tu centro de comando. Cada alerta se prioriza por urgencia y se puede atender en un click.
        </p>
      </header>

      <AlertsHeaderBand
        summary={summaryQuery.data}
        activeFilter={bucketFilter}
        onFilter={setBucketFilter}
        loading={summaryQuery.isLoading}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        {/* Lista principal. Es un <div>, no <main>: el landmark main lo
            provee AppShell y anidar otro es ARIA invalido (#497). */}
        <div>
          {alertsQuery.isLoading && <ListSkeleton />}
          {alertsQuery.isError && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
              Error cargando alertas:{" "}
              {alertsQuery.error instanceof Error ? alertsQuery.error.message : "desconocido"}
            </div>
          )}
          {alertsQuery.data && alertsQuery.data.items.length === 0 && !bucketFilter && (
            <EmptyState />
          )}
          {alertsQuery.data && (alertsQuery.data.items.length > 0 || bucketFilter) && (
            <div className="space-y-6">
              {/* #564: el backend acota la lista (max 200) y manda el total
                  sin paginar. Si aun asi quedo recortada, se dice cuantas
                  hay y como llegar a las demas en vez de callarlo. */}
              {alertsQuery.data.total > alertsQuery.data.items.length && (
                <p
                  data-testid="alerts-truncated-note"
                  className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800"
                >
                  Mostrando las {alertsQuery.data.items.length} alertas más recientes de{" "}
                  {alertsQuery.data.total}. Atiende o pospón alertas para ver las demás.
                </p>
              )}
              {filteredBuckets.map((b) => {
                const bucket = grouped.buckets[b];
                if (bucket.length === 0) {
                  // Con filtro activo, un bucket vacio no puede dejar la
                  // columna muda (#562): se pinta el vacio propio del grupo.
                  return bucketFilter ? (
                    <BucketEmptyState key={b} bucket={b} onClear={() => setBucketFilter(null)} />
                  ) : null;
                }
                return (
                  <section key={b}>
                    <h2 className="mb-2 text-[11px] font-bold tracking-widest text-slate-500 uppercase">
                      {BUCKET_LABELS[b]} ({bucket.length})
                    </h2>
                    <ul className="space-y-2" data-testid={`bucket-${b}`}>
                      {bucket.map((a) => (
                        <AlertCard key={a.id} alert={a} municipality={activeDane} bucket={b} />
                      ))}
                    </ul>
                  </section>
                );
              })}
              {grouped.snoozed.length > 0 && (
                <section>
                  <h2 className="mb-2 text-[11px] font-bold tracking-widest text-slate-400 uppercase">
                    Pospuestas ({grouped.snoozed.length})
                  </h2>
                  <ul className="space-y-2" data-testid="bucket-snoozed">
                    {grouped.snoozed.map((a) => (
                      <AlertCard
                        key={a.id}
                        alert={a}
                        municipality={activeDane}
                        bucket="info"
                        readonly
                      />
                    ))}
                  </ul>
                </section>
              )}
              {includeAttended && grouped.attended.length > 0 && (
                <section>
                  <h2 className="mb-2 text-[11px] font-bold tracking-widest text-slate-400 uppercase">
                    Histórico ({grouped.attended.length})
                  </h2>
                  <ul className="space-y-2" data-testid="bucket-attended">
                    {grouped.attended.map((a) => (
                      <AlertCard
                        key={a.id}
                        alert={a}
                        municipality={activeDane}
                        bucket="info"
                        readonly
                      />
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <AlertsSidebar
          includeAttended={includeAttended}
          onToggleAttended={() => setIncludeAttended((v) => !v)}
          summary={summaryQuery.data}
        />
      </div>
    </div>
  );
}

interface AlertsHeaderBandProps {
  summary: AlertSummary | undefined;
  activeFilter: Bucket | null;
  onFilter: (b: Bucket | null) => void;
  loading: boolean;
}

function AlertsHeaderBand({ summary, activeFilter, onFilter, loading }: AlertsHeaderBandProps) {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-3 gap-3">
        <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
        <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
        <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }

  const chips: Array<{ bucket: Bucket; count: number; label: string; color: string }> = [
    {
      bucket: "hoy",
      count: summary.critical_today,
      label: "Críticas hoy",
      color:
        summary.critical_today > 0
          ? "border-red-300 bg-red-50 text-red-800"
          : "border-slate-200 bg-white text-slate-500",
    },
    {
      bucket: "semana",
      count: summary.this_week,
      label: "Esta semana",
      color:
        summary.this_week > 0
          ? "border-amber-300 bg-amber-50 text-amber-800"
          : "border-slate-200 bg-white text-slate-500",
    },
    {
      bucket: "info",
      count: summary.informational,
      label: "Informativas",
      color: "border-slate-200 bg-white text-slate-700",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="alerts-summary-band">
      {chips.map((c) => {
        const isActive = activeFilter === c.bucket;
        return (
          <button
            key={c.bucket}
            type="button"
            onClick={() => onFilter(isActive ? null : c.bucket)}
            aria-pressed={isActive}
            className={`flex items-baseline justify-between rounded-xl border px-4 py-3 text-left transition-all hover:shadow-xs ${c.color} ${
              isActive ? "ring-2 ring-brand-500 ring-offset-1" : ""
            } cursor-pointer`}
            data-testid={`band-chip-${c.bucket}`}
          >
            <div>
              <div className="text-3xl font-extrabold tabular-nums">{c.count}</div>
              <div className="text-[11px] font-semibold tracking-wide uppercase">{c.label}</div>
            </div>
            {isActive && <span className="text-[10px] font-medium text-brand-700">filtrando</span>}
          </button>
        );
      })}
    </div>
  );
}

interface AlertCardProps {
  alert: AlertDTO;
  municipality: string;
  bucket: Bucket;
  readonly?: boolean;
}

/** Copy cuando el PATCH falla. Dice que paso y que hacer (Sprint 21-9). */
const ACTION_ERROR_COPY = {
  ack: "No pudimos marcar la alerta como atendida. Recarga la página e intenta de nuevo.",
  snooze_week: "No pudimos posponer la alerta. Recarga la página e intenta de nuevo.",
  gone: "Esta alerta ya fue atendida por alguien más de tu equipo o ya no está disponible. Actualizamos la lista.",
} as const;

function AlertCard({ alert, municipality, bucket, readonly = false }: AlertCardProps) {
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const refreshAlerts = () => {
    queryClient.invalidateQueries({ queryKey: ["alerts", municipality] });
    queryClient.invalidateQueries({ queryKey: ["alerts-summary", municipality] });
  };

  // #613: sin onError el fallo del PATCH se tragaba y la tarjeta seguia
  // como activa. Un 404 significa "ya no esta activa" (otro miembro del
  // tenant la atendio; el backend es idempotente via atendida_at IS NULL):
  // no hay nada que reintentar, se avisa y se vuelve a pedir la lista.
  const onActionError = (action: "ack" | "snooze_week") => (err: unknown) => {
    if (err instanceof ApiError && err.status === 404) {
      setActionError(ACTION_ERROR_COPY.gone);
      refreshAlerts();
      return;
    }
    setActionError(ACTION_ERROR_COPY[action]);
  };

  const ackMutation = useMutation({
    mutationFn: () =>
      patchAlertAction({
        alertId: alert.id,
        municipality,
        action: "ack",
      }),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      track({
        event_name: "alert_acked",
        properties: { alert_id: alert.id, alert_type: alert.alert_type, bucket },
      });
      refreshAlerts();
    },
    onError: onActionError("ack"),
  });

  const snoozeMutation = useMutation({
    mutationFn: () =>
      patchAlertAction({
        alertId: alert.id,
        municipality,
        action: "snooze_week",
      }),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      track({
        event_name: "alert_snoozed",
        properties: { alert_id: alert.id, alert_type: alert.alert_type, bucket },
      });
      refreshAlerts();
    },
    onError: onActionError("snooze_week"),
  });

  const ctaHref = alert.cta_href ?? "/radar";
  const isExternal = ctaHref.startsWith("http://") || ctaHref.startsWith("https://");

  const onCtaClick = () => {
    track({
      event_name: "alert_cta_clicked",
      properties: {
        alert_id: alert.id,
        alert_type: alert.alert_type,
        cta_href: ctaHref,
        bucket,
      },
    });
  };

  const acted = ackMutation.isPending || snoozeMutation.isPending;
  const palette = SEVERITY_PALETTE[alert.severity];

  return (
    <li
      data-testid="alert-card"
      data-severity={alert.severity}
      data-alert-type={alert.alert_type}
      className={`rounded-xl border bg-white p-4 shadow-xs transition-opacity ${
        readonly || alert.atendida_at ? "opacity-60" : ""
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          aria-label={SEVERITY_LABELS[alert.severity]}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase ring-1 ring-inset ${palette}`}
        >
          <span aria-hidden="true">{SEVERITY_ICONS[alert.severity]}</span>
          {SEVERITY_LABELS[alert.severity]}
        </span>
        <span
          data-testid="alert-type-label"
          className="text-[11px] font-medium tracking-wide text-slate-400 uppercase"
        >
          {alertTypeLabel(alert.alert_type)}
        </span>
        <span className="ml-auto text-xs text-slate-500">{formatRelative(alert.created_at)}</span>
      </div>

      <h2 className="text-sm font-bold text-slate-900">{alert.title}</h2>
      <p className="mt-1 text-sm text-slate-600">{alert.body}</p>

      {!readonly && !alert.atendida_at && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {isExternal ? (
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCtaClick}
              className="cursor-pointer rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-brand-800"
            >
              Ver más →
            </a>
          ) : (
            <Link
              to={ctaHref}
              onClick={onCtaClick}
              className="cursor-pointer rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-brand-800"
              data-testid="alert-cta"
            >
              Ver más →
            </Link>
          )}
          <button
            type="button"
            onClick={() => ackMutation.mutate()}
            disabled={acted}
            className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
            data-testid="alert-ack"
          >
            Atendida
          </button>
          <button
            type="button"
            onClick={() => snoozeMutation.mutate()}
            disabled={acted}
            className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
            data-testid="alert-snooze"
          >
            Posponer 1 sem
          </button>
        </div>
      )}

      {actionError && (
        <p
          role="alert"
          data-testid="alert-action-error"
          className="mt-2 text-xs font-medium text-red-700"
        >
          {actionError}
        </p>
      )}

      {alert.atendida_at && (
        <p className="mt-2 text-[11px] font-medium text-slate-400">
          Atendida {formatRelative(alert.atendida_at)}
        </p>
      )}
      {alert.snoozed_until && isSnoozed(alert) && (
        <p
          className="mt-2 text-[11px] font-medium text-slate-400"
          data-testid="alert-snoozed-until"
        >
          Pospuesta hasta el {formatDateEs(alert.snoozed_until, { day: "numeric", month: "long" })}
        </p>
      )}
    </li>
  );
}

interface AlertsSidebarProps {
  includeAttended: boolean;
  onToggleAttended: () => void;
  summary: AlertSummary | undefined;
}

function AlertsSidebar({ includeAttended, onToggleAttended, summary }: AlertsSidebarProps) {
  return (
    <aside className="lg:sticky lg:top-4 lg:self-start">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
        <h3 className="text-xs font-bold tracking-wide text-slate-500 uppercase">Vista</h3>
        <button
          type="button"
          onClick={onToggleAttended}
          className="mt-3 inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700 hover:text-brand-700"
        >
          <span
            className={`flex h-4 w-7 items-center rounded-full p-0.5 transition-colors ${
              includeAttended ? "bg-brand-600" : "bg-slate-300"
            }`}
          >
            <span
              className={`block h-3 w-3 rounded-full bg-white transition-transform ${
                includeAttended ? "translate-x-3" : ""
              }`}
            />
          </span>
          Mostrar histórico ({summary?.attended_this_week ?? 0} de la semana)
        </button>
        <p className="mt-3 text-xs leading-relaxed text-slate-500">
          Las alertas atendidas o pospuestas no se eliminan — se mueven al histórico y puedes
          traerlas de vuelta con este toggle.
        </p>
      </div>

      <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
        <h3 className="text-xs font-bold tracking-wide text-slate-500 uppercase">
          Cómo se generan
        </h3>
        <p className="mt-2 text-xs leading-relaxed text-slate-600">
          El sistema escanea cada noche tu muni, tus peers y las convocatorias activas. Si algo
          amerita acción, aparece acá. Sin spam: máximo 1 alerta por situación cada 7 días.
        </p>
      </div>
    </aside>
  );
}

function ListSkeleton() {
  return (
    <ul className="space-y-3" data-testid="alerts-skeleton">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="h-24 animate-pulse rounded-xl border border-slate-200 bg-slate-50" />
      ))}
    </ul>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="alerts-empty"
      className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center"
    >
      <p className="text-sm font-medium text-slate-700">Sin alertas activas por ahora.</p>
      <p className="mt-1 text-xs text-slate-500">
        Se generan automáticamente cada día a las 6:30 AM. Si quieres adelantarte, revisa el Radar.
      </p>
      <Link
        to="/radar"
        className="mt-4 inline-block text-xs font-semibold text-brand-700 hover:underline"
      >
        Ir al Radar →
      </Link>
    </div>
  );
}

const BUCKET_EMPTY_COPY: Record<Bucket, string> = {
  hoy: "No tienes alertas críticas hoy.",
  semana: "Nada vence esta semana.",
  info: "No tienes alertas informativas.",
};

interface BucketEmptyStateProps {
  bucket: Bucket;
  onClear: () => void;
}

/**
 * Vacio propio de un grupo cuando el usuario filtra por un chip sin alertas
 * (#562). Dice que ese grupo esta vacio y ofrece el camino de salida.
 */
function BucketEmptyState({ bucket, onClear }: BucketEmptyStateProps) {
  return (
    <div
      data-testid={`alerts-empty-${bucket}`}
      className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center"
    >
      <p className="text-sm font-medium text-slate-700">{BUCKET_EMPTY_COPY[bucket]}</p>
      <p className="mt-1 text-xs text-slate-500">
        Quita el filtro para ver el resto de tus alertas, o revisa el Radar.
      </p>
      <div className="mt-4 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={onClear}
          data-testid="alerts-empty-clear-filter"
          className="cursor-pointer text-xs font-semibold text-brand-700 hover:underline"
        >
          Ver todas las alertas
        </button>
        <Link to="/radar" className="text-xs font-semibold text-brand-700 hover:underline">
          Ir al Radar →
        </Link>
      </div>
    </div>
  );
}
